import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect } from "react";

import { useForm } from "react-hook-form";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { Input } from "shared/components/inputs/Input";

import PageContainer from "shared/components/pagecontainer/PageContainer";

import { z } from "zod";

import styles from "./EditarEndereco.module.scss";

import {
  atualizarEndereco,
  buscarEnderecoPorId,
} from "shared/services/api/endereco.api";

import { buscarEnderecoPorCep } from "shared/services/api/cep.api";

import { maskCep } from "shared/utils/masks";

const schema = z.object({
  nome: z.string().min(
    2,
    "Nome do endereço é obrigatório"
  ),
  cep: z.string().min(
    8,
    "CEP inválido"
  ),
  rua: z.string().min(
    2,
    "Rua é obrigatória"
  ),
  numero: z.string().min(
    2,
    "Número é obrigatório"
  ),
  complemento: z.string().optional(),
  bairro: z.string().min(
    2,
    "Bairro é obrigatório"
  ),
  cidade: z.string().min(
    2,
    "Cidade é obrigatória"
  ),
  estado: z.string().min(
    2,
    "Estado é obrigatório"
  ),
});

type FormData = z.infer<typeof schema>;

interface CadastroLojaState {
  origem?: string;
  returnTo?: string;
  selecionarEndereco?: boolean;
  dadosInformacoes?: unknown;
  dadosRepresentante?: unknown;
  dadosEndereco?: unknown;
  abaRetorno?: string;
}

export const EditarEndereco = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const location = useLocation();

  const navigationState =
    location.state as CadastroLojaState | null;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  /*
   * =========================================
   * CARREGAR ENDEREÇO
   * =========================================
   */

  useEffect(() => {
    async function carregarEndereco() {
      if (!id) {
        return;
      }

      const endereco =
        await buscarEnderecoPorId(id);

      if (!endereco) {
        return;
      }

      const cep = endereco.cep;

      setValue(
        "nome",
        endereco.nome
      );

      setValue(
        "cep",
        cep
      );

      setValue(
        "rua",
        endereco.rua
      );

      setValue(
        "numero",
        endereco.numero
      );

      setValue(
        "complemento",
        endereco.complemento
      );

      setValue(
        "bairro",
        endereco.bairro
      );

      setValue(
        "cidade",
        endereco.cidade
      );

      setValue(
        "estado",
        endereco.estado
      );

      /*
       * CEP automático ao abrir.
       */

      const cepLimpo =
        cep.replace(/\D/g, "");

      if (cepLimpo.length === 8) {
        const data =
          await buscarEnderecoPorCep(
            cepLimpo
          );

        if (data) {
          setValue(
            "rua",
            data.rua
          );

          setValue(
            "bairro",
            data.bairro
          );

          setValue(
            "cidade",
            data.cidade
          );

          setValue(
            "estado",
            data.estado
          );
        }
      }
    }

    carregarEndereco();
  }, [id, setValue]);

  /*
   * =========================================
   * SALVAR
   * =========================================
   */

  const onSubmit = async (
    data: FormData
  ) => {
    try {
      if (!id) {
        return;
      }

      await atualizarEndereco(
        id,
        data
      );

      alert(
        "Endereço atualizado com sucesso!"
      );

      /*
       * =========================================
       * CADASTRO DA LOJA
       * =========================================
       *
       * Mantém todo o contexto que veio de
       * MeusEnderecos.
       */
      if (
        navigationState?.origem ===
          "cadastro-loja" &&
        navigationState.returnTo
      ) {
        navigate(
          "/meusenderecos",
          {
            state: {
              ...navigationState,
              enderecoId: id,
            },
          }
        );

        return;
      }

      /*
       * =========================================
       * FLUXO NORMAL
       * =========================================
       *
       * Mantém o comportamento existente.
       */
      navigate(
        "/meusenderecos",
        {
          state:
            location.state,
        }
      );
    } catch (error) {
      console.error(error);

      alert(
        "Erro ao atualizar endereço."
      );
    }
  };

  return (
    <PageContainer
      title="Editar Endereço"
      showDesktopSearch={false}
    >
      <div className={styles.page}>
        <form
          onSubmit={handleSubmit(
            onSubmit
          )}
          className={styles.card}
        >
          {/* NOME */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Nome do Endereço
            </label>

            <Input
              name="nome"
              type="text"
              placeholder="Ex: Casa, Trabalho"
              register={register(
                "nome"
              )}
              error={
                errors.nome?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* CEP */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              CEP
            </label>

            <Input
              name="cep"
              type="text"
              placeholder="Digite o CEP"
              register={register(
                "cep",
                {
                  onChange: async (
                    e
                  ) => {
                    const value =
                      maskCep(
                        e.target.value
                      );

                    e.target.value =
                      value;

                    const cepLimpo =
                      value.replace(
                        /\D/g,
                        ""
                      );

                    if (
                      cepLimpo.length ===
                      8
                    ) {
                      const endereco =
                        await buscarEnderecoPorCep(
                          cepLimpo
                        );

                      if (endereco) {
                        setValue(
                          "rua",
                          endereco.rua
                        );

                        setValue(
                          "bairro",
                          endereco.bairro
                        );

                        setValue(
                          "cidade",
                          endereco.cidade
                        );

                        setValue(
                          "estado",
                          endereco.estado
                        );
                      }
                    }
                  },
                }
              )}
              error={
                errors.cep?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* RUA */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Rua
            </label>

            <Input
              name="rua"
              type="text"
              placeholder="Digite a rua"
              register={register(
                "rua"
              )}
              error={
                errors.rua?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* NÚMERO */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Número
            </label>

            <Input
              name="numero"
              type="text"
              placeholder="Digite o número"
              register={register(
                "numero"
              )}
              error={
                errors.numero?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* COMPLEMENTO */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Complemento
            </label>

            <Input
              name="complemento"
              type="text"
              placeholder="Apartamento, bloco..."
              register={register(
                "complemento"
              )}
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* BAIRRO */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Bairro
            </label>

            <Input
              name="bairro"
              type="text"
              placeholder="Digite o bairro"
              register={register(
                "bairro"
              )}
              error={
                errors.bairro?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* CIDADE */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Cidade
            </label>

            <Input
              name="cidade"
              type="text"
              placeholder="Digite a cidade"
              register={register(
                "cidade"
              )}
              error={
                errors.cidade?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* ESTADO */}

          <div
            className={
              styles.containerLabel
            }
          >
            <label
              className={styles.label}
            >
              Estado
            </label>

            <Input
              name="estado"
              type="text"
              placeholder="Ex: MG"
              register={register(
                "estado"
              )}
              error={
                errors.estado?.message
              }
              customClass={
                styles.customInput
              }
            />
          </div>

          {/* BOTÕES */}

          <div
            className={styles.buttons}
          >
            <button
              type="button"
              className={styles.cancel}
              onClick={() =>
                navigate(-1)
              }
            >
              Cancelar
            </button>

            <button
              type="submit"
              className={styles.save}
            >
              Atualizar Endereço
            </button>
          </div>
        </form>
      </div>
    </PageContainer>
  );
};